<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-stepper :steps="rmSteps" :current="currentStep" />
        <hm-spinner v-if="!ready" />
        <Loading :active="busy" :is-full-page="false">
          <slot>
            <hm-spinner />
          </slot>
        </Loading>
        <hm-alert />

        <b-form @submit.prevent="onSubmit" v-show="ready">
          <h4>Step 3: Determine Risk Level</h4>
          <hr />
          <p>Go through each threat-vulnerability combination to determine level of risk.</p>

          <b-form-group>
            <b-table class="p-1" responsive bordered hover show-empty empty-text="No Threat Vulnerability" :items="threats" :fields="columns" @row-clicked="rowClick">
              <template slot="cell(threat)" slot-scope="data">
                {{data.item.threat}}
                <hm-popover v-if="data.item.threatInfo" :title="data.item.threat" :content="data.item.threatInfo" />
              </template>
              <template slot="cell(vuln)" slot-scope="data">
                {{data.item.vuln}}
                <hm-popover v-if="data.item.vulnInfo" :title="data.item.vuln" :content="data.item.vulnInfo" />
              </template>
              <template slot="cell(risk)" slot-scope="data">
                <strong v-if="data.item.approved">{{data.item.risk}}</strong>
                <b-badge v-else variant="danger">START</b-badge>
              </template>
            </b-table>
          </b-form-group>

          <hr />
          <b-row>
            <b-col cols="6">
              <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/risk/threats">Back</b-button>
            </b-col>
            <b-col cols="6" class="text-right">
              <b-button type="submit" variant="primary" class="px-4" :disabled="busy || this.activeThreats == 0 || this.unapprovedThreats > 0">Next</b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { riskService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'RiskAnalysisLevelList',
  data() {
    return {
      rm: {
        threats: []
      },
      threats: [],
      columns: [{
        key: 'threat',
        label: 'Threat'
      }, {
        key: 'vuln',
        label: 'Vulnerability'
      }, {
        key: 'risk',
        label: 'Risk',
        tdClass: (value, key, item) => {
          return ['text-center', (item.approved ? 'risk-' + value : '')];
        }
      }],
      ready: false,
      busy: false,
      submitted: false,
      currentStep: 3
    }
  },
  computed: {
    ...mapState(['rmSteps']),
    ...mapState('maps', ['threatMap']),
    activeThreats() {
      return Enumerable.from(this.threats).where(x => { return x.active }).count();
    },
    approvedThreats() {
      return Enumerable.from(this.threats).where(x => { return (x.active && x.approved) }).count();
    },
    unapprovedThreats() {
      return Enumerable.from(this.threats).where(x => { return (x.active && !x.approved) }).count();
    }
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertException: 'alert/exception'
    }),
    rowClick(record, index) {
      this.busy = true;
      this.alertClear();

      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.$router.push({ name: 'RiskAnalysisLevel', params: { id: record.id } });
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();
      this.rm.step = this.currentStep;

      riskService.setRiskManagement(this.rm)
        .then(data => {
          this.$router.push({ name: 'RiskAnalysisReport', params: { id: data.id } });
        })
        .catch(err => {
          this.alertException({ err, message: 'Error saving risk management.' });
        })
        .finally(() => {
          this.busy = false;
        });
    }
  },
  created() {
    this.ready = false;

    Promise.all([
      this.$store.dispatch('maps/fetchThreatMap'),
      riskService.getCurrentRiskManagement()
    ]).then((values) => {
      this.rm = values[1];

      this.threats = Enumerable.from(this.threatMap)
        .join(this.rm.threats,
          function (o) { return o.id + ':' + o.revision; },
          function (i) { return i.id + ':' + i.revision; },
          function (o, i) { return { id: o.id, revision: o.revision, threat: o.threat, threatInfo: o.threatInfo, vuln: o.vuln, vulnInfo: o.vulnInfo, active: i.active, approved: i.approved, risk: i.chance * i.impact }; })
        .where(function (x) { return x.active; })
        .toArray();
    }).catch((err) => {
      this.alertException({ err, message: 'Error loading risk management data.' });
    }).finally(() => {
      this.ready = true;
    });
  }
}
</script>

<style lang="scss">
// import variables for this application
@import '@/assets/scss/_variables.scss';

/* risk colors */
.risk-1 {
  color: white;
  background-color: #00ff33;
}
.risk-2 {
  color: white;
  background-color: #00ff33;
}
.risk-3 {
  color: white;
  background-color: #33cc00;
}
.risk-4 {
  color: white;
  background-color: #33cc00;
}
.risk-5 {
  color: white;
  background-color: #33cc00;
}
.risk-6 {
  color: white;
  background-color: #99cc33;
}
.risk-8 {
  color: white;
  background-color: #ffcc00;
}
.risk-9 {
  color: white;
  background-color: #ffcc00;
}
.risk-10 {
  color: white;
  background-color: #ff9900;
}
.risk-12 {
  color: white;
  background-color: #ff6633;
}
.risk-15 {
  color: white;
  background-color: #cc0000;
}
.risk-16 {
  color: white;
  background-color: #990000;
}
.risk-20 {
  color: white;
  background-color: #330000;
}
.risk-25 {
  color: white;
  background-color: #000000;
}
</style>